/**
 * Per-design security identity (2026-11-04, Princewill's ruling:
 * the designs must NOT all carry the same security write-up).
 *
 * Every bank template's specs.securityFeatures is a list of
 * marketing-style strings. Until now the renderer ignored them:
 * every design opened with the factory feature set, so every face —
 * and every dynamic back panel — carried the same security mix.
 * Applying a template now sets the front security-feature defaults
 * from THAT template's own spec list, mapped through the table
 * below to the feature ids the renderer actually draws (each id
 * here is gated by isSecurityEnabled in CheckBuilder's renderer —
 * microprint border/warning text, guilloche, VOID pantograph,
 * watermark, UV markers, holographic strip, chemical-reactive
 * indicator, thermochromic medallion, warning band, anti-copy
 * V-dot, prismatic column, microprint signature line, CPSA
 * padlock). The back panel then follows the enabled set (see
 * backVariants.ts), so each design's security write-up differs
 * because its face genuinely differs.
 *
 * Honesty rules:
 * - A spec string maps ONLY where the renderer draws that feature.
 *   Everything else is listed in UNMAPPED_SPEC_STRINGS with the
 *   reason class (artwork, texture, paper property, print process,
 *   or verification scheme the feature system does not render) —
 *   never silently upgraded into a printed claim.
 * - 'micr_clear_band' is structural (a layout guard for the ANSI
 *   clear band), not a design feature: template application never
 *   touches it — it keeps the user's/factory setting.
 * - User toggles win from the moment of application: this module
 *   only computes the design's DEFAULT set at apply time. The
 *   result lives in the normal securityFeatures state, so user
 *   edits override it and snapshots persist the outcome.
 * - Templates with no spec list (the premium designs carry colours
 *   only) return null: no honest basis to differentiate, so the
 *   current set is left untouched.
 * - The factory BLANK default is unchanged (factoryCheckSnapshot);
 *   this is per-template differentiation only.
 */

import { bankSpecificTemplates } from '../data/bankTemplates';

export interface SecurityFeatureEntry {
    id: string;
    name: string;
    enabled: boolean;
}

/** Spec string -> renderer feature id. ONLY renderable features. */
export const SPEC_FEATURE_MAP: Record<string, string> = {
    // UV family — the panel wording map (backVariants PANEL_WORDING)
    // already groups exactly these strings under the uv_markers
    // front feature ('FLUORESCENT FIBER TECHNOLOGY').
    'uv-fibers': 'uv_markers',
    'uv-reactive': 'uv_markers',
    'uv-reactive-ink': 'uv_markers',
    'fluorescent-fibers': 'uv_markers',
    'blue-fluorescent-fibers': 'uv_markers',
    // Chemical-reactive paper — the face draws the indicator.
    'chemical-reactive': 'chemical_reactive',
    'chemical-reactive-paper': 'chemical_reactive',
    'chemical-sensitive': 'chemical_reactive',
    'chemical-sensitive-paper': 'chemical_reactive',
    'react-to-bleach': 'chemical_reactive',
    // Microprint family.
    'microprinting': 'microprint',
    'microprint-border': 'microprint',
    'microprint-lines': 'microprint',
    'basic-microprinting': 'microprint',
    'micro-text': 'microprint',
    'microprint-signature': 'microprint_sig',
    // VOID pantograph / security screen.
    'void-pantograph': 'void_pantograph',
    'void-pantograph-heavy': 'void_pantograph',
    'security-screentone': 'void_pantograph',
    // Watermark variants — the renderer draws the design's watermark.
    'watermark': 'watermark',
    'watermark-seal': 'watermark',
    'check-stock-watermark': 'watermark',
    'maple-leaf-watermark': 'watermark',
    'crown-watermark': 'watermark',
    'watermark-lion': 'watermark',
    'watermark-eagle': 'watermark',
    'watermark-liberty': 'watermark',
    // Holographic family — the renderer draws one holographic
    // element (the strip); thread/foil/dual/square variants of the
    // claim render as that element, as the panel wording map
    // already groups them under the one holographic line.
    'holographic-thread': 'holographic',
    'holographic-foil': 'holographic',
    'holographic-strip': 'holographic',
    'holographic-band': 'holographic',
    'holographic-eagle': 'holographic',
    'holographic-stamp': 'holographic',
    'square-hologram': 'holographic',
    'dual-hologram': 'holographic',
    'hologram': 'holographic',
    'hologram-logo': 'holographic',
    // Thermochromic family — the medallion/seal the face draws.
    'thermochromic-ink': 'thermochromic',
    'thermal-ink': 'thermochromic',
    'heat-reactive': 'thermochromic',
    // Guilloche.
    'guilloche-pattern': 'guilloche',
    'guilloche-ultra': 'guilloche',
    // Padlock.
    'secure-padlock': 'cpsa_padlock',
    'lock-icon-security': 'cpsa_padlock',
    // Prismatic (no current template uses the bare string; kept so
    // the vocabulary stays complete with the panel map).
    'prismatic': 'prismatic_column'
};

/**
 * Every spec string in the template catalogue that deliberately
 * does NOT enable a front feature, grouped by why. A test pins
 * that every spec string used by any template is either in
 * SPEC_FEATURE_MAP or named here — nothing is silently dropped.
 */
export const UNMAPPED_SPEC_STRINGS: Record<string, string[]> = {
    // Template artwork / furniture — drawn (or not) by the template's
    // own face branches, never by the toggleable feature system.
    artwork: ['statue-of-liberty', 'treasury-seal', 'shaded-signature-box', 'embossed-seal', 'dual-signature', 'dual-stub-layout', 'blue-safety-band', 'sterling-symbol', 'photo-safe-deposit', 'bottom-security-statement'],
    // Paper / texture properties — the texture and paper systems'
    // territory, not a toggleable front feature.
    textureOrPaper: ['marble-cloud-texture', 'marble-texture', 'parchment-texture', 'fiber-optic-thread', 'security-fibers', 'toner-adhesion', 'uv-dull-paper', 'planchettes', 'erasure-protection'],
    // Print processes / ink technologies the canvas does not perform.
    printProcess: ['color-shifting-ink', 'optic-variable-ink', 'iris-printing', 'rainbow-printing', 'rainbow-border', 'intaglio-printing', 'tactile-printing', 'moire-pattern', 'latent-image', 'anti-stockton', 'dna-ink', 'bleeding-ink-micr', 'magnetic-ink-micr', 'fugitive-ink'],
    // Physical constructions (threads, perforations, seals) that are
    // not drawn elements of the face.
    construction: ['security-thread', 'micro-perforations', 'perforation-lines', 'stub-perforations', 'tamper-evident-seal', 'vertical-security-bar', 'vertical-bofa-security-strip', 'security-weave', 'security-border'],
    // Verification / data schemes — nothing on the face renders them.
    verification: ['digital-signature', 'digital-signature-hash', 'digital-watermark', 'qr-verification', 'qr-code-verification', 'blockchain-hash', 'timestamp-encryption', 'sequential-numbering', 'copy-detection-pattern', 'dual-image-technology', 'bilingual-text']
};

export const ALL_UNMAPPED_SPEC_STRINGS: string[] = Object.values(UNMAPPED_SPEC_STRINGS)
    .reduce<string[]>((acc, list) => acc.concat(list), []);

/** Feature ids a design application may set. micr_clear_band is
 *  structural and deliberately absent. */
export const DESIGN_FEATURE_IDS: string[] = Array.from(new Set(Object.values(SPEC_FEATURE_MAP)));

/**
 * The renderer feature ids a template's own spec list honestly
 * enables. Returns null when the id is not a bank template or the
 * template carries no spec list — the caller then leaves the
 * current feature set untouched.
 */
export function securityFeatureIdsForTemplate(bankId: string | null | undefined): string[] | null {
    if (!bankId) return null;
    const template = (bankSpecificTemplates as any)[bankId];
    const specs: string[] | undefined = template?.specs?.securityFeatures;
    if (!template || !Array.isArray(specs)) return null;
    const ids = new Set<string>();
    for (const s of specs) {
        const fid = SPEC_FEATURE_MAP[s];
        if (fid) ids.add(fid);
    }
    return Array.from(ids);
}

/**
 * The feature list after applying a template's default set: mapped
 * features on, every other design feature off, micr_clear_band (and
 * any unknown future entry) untouched. Returns null when the
 * template has no derivable set (see above).
 */
export function applyTemplateSecuritySet(
    current: SecurityFeatureEntry[],
    bankId: string | null | undefined
): SecurityFeatureEntry[] | null {
    const ids = securityFeatureIdsForTemplate(bankId);
    if (ids === null) return null;
    const on = new Set(ids);
    return current.map(f => {
        if (f.id === 'micr_clear_band') return f;
        if (!KNOWN_DESIGN_FEATURES.has(f.id)) return f;
        return { ...f, enabled: on.has(f.id) };
    });
}

/** All renderer design features (the factory list minus the
 *  structural micr_clear_band) — a feature with no mapping anywhere
 *  (warning_band, anti_copy_vdot) is still a design feature: a
 *  template whose specs never name it turns it OFF. */
const KNOWN_DESIGN_FEATURES = new Set([
    'microprint', 'guilloche', 'void_pantograph', 'watermark', 'uv_markers',
    'holographic', 'chemical_reactive', 'thermochromic', 'warning_band',
    'anti_copy_vdot', 'prismatic_column', 'microprint_sig', 'cpsa_padlock'
]);
